import { Box, Flex, Heading, Tag, Progress, Text, Grid } from '@chakra-ui/react';
import { InfoIcon } from '@chakra-ui/icons';
import { MemoryInfo } from '../../types/stats';
import { parseMemoryMB, parseStorageGB, getAlertColor, getProgressColor } from '../../utils/stats';
import { FaMemory } from 'react-icons/fa';

interface MemoryCardProps {
  data: MemoryInfo;
  paused: boolean;
}

export function MemoryCard({ data, paused }: MemoryCardProps) {
  // Parse memory values - handle both GB and MB
  let totalGB, usedGB, freeGB;
  
  if (data.total.includes('GB')) {
    totalGB = parseStorageGB(data.total);
    usedGB = parseStorageGB(data.used);
    freeGB = parseStorageGB(data.free);
  } else {
    // Convert from MB to GB
    totalGB = parseMemoryMB(data.total) / 1024;
    usedGB = parseMemoryMB(data.used) / 1024;
    freeGB = parseMemoryMB(data.free) / 1024;
  }
  
  const usagePercentage = totalGB > 0 ? (usedGB / totalGB) * 100 : 0;
  const alertColor = getAlertColor(usagePercentage);
  const progressColor = getProgressColor(usagePercentage);

  return (
    <Box borderWidth="1px" borderRadius="2xl" p={4} position="relative" overflow="hidden">
      {paused && (
        <Tag borderRadius="2xl" position="absolute" top={2} right={2} colorScheme="yellow">Paused</Tag>
      )}
      
      <Flex as="header" alignItems="center" justifyContent="space-between" pb={2}>
        <Heading as="h3" size="sm" display="flex" alignItems="center" gap={2}>
          <FaMemory />
          Memory
        </Heading>
        <Tag borderRadius="2xl" colorScheme={alertColor === 'danger' ? 'red' : alertColor === 'warning' ? 'yellow' : 'green'}>
          {Math.round(usagePercentage)}%
        </Tag>
      </Flex>
      
      <Box>
        {/* Memory Usage Bar */}
        <Box>
          <Flex justifyContent="space-between" fontSize="sm">
            <Text color="gray.500">Used</Text>
            <Text fontFamily="mono">{data.used} / {data.total}</Text>
          </Flex>
          <Progress borderRadius={"xl"} value={usagePercentage} colorScheme={alertColor === 'danger' ? 'red' : alertColor === 'warning' ? 'yellow' : 'green'} size="sm" mt={2} />
        </Box>

        {/* Memory Stats Grid */}
        <Grid templateColumns="repeat(3, 1fr)" gap={4} textAlign="center" mt={4}>
          <Box>
            <Text fontSize="2xl" fontWeight="bold" fontFamily="mono">{totalGB.toFixed(1)}</Text>
            <Text fontSize="xs" color="gray.500">Total GB</Text>
          </Box>
          <Box>
            <Text fontSize="2xl" fontWeight="bold" fontFamily="mono" color={progressColor}>
              {usedGB.toFixed(1)}
            </Text>
            <Text fontSize="xs" color="gray.500">Used GB</Text>
          </Box>
          <Box>
            <Text fontSize="2xl" fontWeight="bold" fontFamily="mono" color="green.500">
              {freeGB.toFixed(1)}
            </Text>
            <Text fontSize="xs" color="gray.500">Free GB</Text>
          </Box>
        </Grid>

      </Box>
    </Box>
  );
}
